import { Box, Stack } from "@mui/material";
import AcxSelect, {
    type DisabledAppearance,
} from "components/UI/Select/BaseSelectComponents/AcxSelect";
import ClassifierGroup from "models/ClassifierGroup";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ClassifierService from "services/ClassifierService";

interface RecursiveGroupSelectorProps {
    /** Currently selected group path, ordered parent -> down ([root, ..., leaf]). */
    value: string[];
    /** Emits the new ordered path whenever the selection changes. */
    onChange(path: string[]): void;
    /** Placeholder for the top-level (first) dropdown. */
    placeholder?: string;
    disabled?: boolean;
    disabledAppearance?: DisabledAppearance;
}

const ROOT = "__root__";

const parentKey = (group: ClassifierGroup): string =>
    group.parentGroupId ?? ROOT;

/**
 * Cascading classifier-group selector. Renders one dropdown for the top-level
 * groups, then an additional dropdown for each level of sub-groups as the user
 * drills down. The selection is represented as an ordered path of group ids
 * (parent -> down); the deepest entry is the effective selection.
 *
 * Self-fetches the flat active group list (like {@link GroupSelector}) and
 * builds the tree client-side via each group's `parentGroupId`.
 */
export function RecursiveGroupSelector(props: RecursiveGroupSelectorProps) {
    const {
        value,
        onChange,
        placeholder,
        disabled,
        disabledAppearance,
    } = props;

    const [groups, setGroups] = useState<ClassifierGroup[]>([]);
    const [viewableGroupIds, setViewableGroupIds] = useState<Set<string>>(
        new Set(),
    );
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const abortController = new AbortController();
        const fetchGroups = async () => {
            try {
                setIsLoading(true);
                const service = new ClassifierService();
                const [allGroups, viewableIds] = await Promise.all([
                    service.getClassifierGroups(abortController.signal),
                    service.getGroupIdsWithViewableClassifiers(
                        abortController.signal,
                    ),
                ]);
                setGroups(allGroups);
                setViewableGroupIds(new Set(viewableIds));
            } catch {
                // request aborted or failed; leave groups empty
            } finally {
                setIsLoading(false);
            }
        };

        fetchGroups();
        return () => abortController.abort();
    }, []);

    // Only show groups that the user has something to see in: a group is kept when it — or any of
    // its descendants — directly contains a viewable classifier. Because the query rolls child-group
    // classifiers up to the parent, we keep the whole ancestor chain of every directly-viewable group.
    const visibleGroups = useMemo(() => {
        if (viewableGroupIds.size === 0) return [];
        const byIdAll = new Map(groups.map((group) => [group.id, group]));
        const keepIds = new Set<string>();
        for (const viewableId of viewableGroupIds) {
            let current = byIdAll.get(viewableId);
            while (current && !keepIds.has(current.id)) {
                keepIds.add(current.id);
                current = current.parentGroupId
                    ? byIdAll.get(current.parentGroupId)
                    : undefined;
            }
        }
        return groups.filter((group) => keepIds.has(group.id));
    }, [groups, viewableGroupIds]);

    const { byId, childrenOf } = useMemo(() => {
        const idMap = new Map<string, ClassifierGroup>();
        const childMap = new Map<string, ClassifierGroup[]>();
        for (const group of visibleGroups) {
            idMap.set(group.id, group);
            const key = parentKey(group);
            const siblings = childMap.get(key) ?? [];
            siblings.push(group);
            childMap.set(key, siblings);
        }
        for (const siblings of childMap.values()) {
            siblings.sort((a, b) => a.name.localeCompare(b.name));
        }
        return {
            byId: idMap,
            childrenOf: (parentId: string | null): ClassifierGroup[] =>
                childMap.get(parentId ?? ROOT) ?? [],
        };
    }, [visibleGroups]);

    // Validate/repair a (possibly stale) persisted path against the live tree:
    // each entry must be a real parent->child edge. Truncate at the first break.
    const validatePath = useMemo(
        () =>
            (path: string[]): string[] => {
                const repaired: string[] = [];
                let expectedParent: string | null = null;
                for (const id of path) {
                    const group = byId.get(id);
                    if (!group) break;
                    if ((group.parentGroupId ?? null) !== expectedParent) break;
                    repaired.push(id);
                    expectedParent = id;
                }
                return repaired;
            },
        [byId],
    );

    // Once groups load, repair the incoming path and emit the correction once.
    const lastEmittedRepair = useRef<string>();
    useEffect(() => {
        if (visibleGroups.length === 0) return;
        const repaired = validatePath(value);
        const repairedKey = repaired.join(",");
        if (
            repairedKey !== value.join(",") &&
            repairedKey !== lastEmittedRepair.current
        ) {
            lastEmittedRepair.current = repairedKey;
            onChange(repaired);
        }
    }, [visibleGroups, value, validatePath, onChange]);

    const handleLevelChange = (levelIndex: number, group?: ClassifierGroup) => {
        if (!group?.id) {
            // "No Selection" / cleared: drop this level and everything below it.
            onChange(value.slice(0, levelIndex));
            return;
        }
        onChange([...value.slice(0, levelIndex), group.id]);
    };

    // Build the dropdown levels: always the top level, then one further level
    // for each selected group that has children, plus an optional (unselected)
    // trailing level when the deepest selection still has sub-groups.
    const levels: Array<{
        parentId: string | null;
        options: ClassifierGroup[];
        selectedId?: string;
        levelIndex: number;
    }> = [];
    let parentId: string | null = null;
    let levelIndex = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const options = childrenOf(parentId);
        if (options.length === 0) break;
        const selectedId = value[levelIndex];
        levels.push({ parentId, options, selectedId, levelIndex });
        if (!selectedId) break;
        parentId = selectedId;
        levelIndex += 1;
    }

    return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {levels.length === 0 ? (
                <Box minWidth="220px">
                    <AcxSelect
                        id="classifier-group-level-0"
                        valueField="id"
                        labelField="name"
                        options={[]}
                        fullWidth
                        isLoading={isLoading}
                        isDisabled={disabled}
                        disabledAppearance={disabledAppearance}
                        placeholder={placeholder ?? "Select group"}
                    />
                </Box>
            ) : (
                levels.map((level) => (
                    <Box
                        key={`level-${level.levelIndex}-${level.parentId ?? ROOT}`}
                        minWidth="220px"
                    >
                        <AcxSelect
                            id={`classifier-group-level-${level.levelIndex}`}
                            valueField="id"
                            labelField="name"
                            options={level.options}
                            defaultValue={
                                level.selectedId
                                    ? level.options.find(
                                          (group) =>
                                              group.id === level.selectedId,
                                      )
                                    : undefined
                            }
                            enableNoSelection={level.levelIndex !== 0}
                            fullWidth
                            isLoading={isLoading}
                            isDisabled={disabled}
                            disabledAppearance={disabledAppearance}
                            placeholder={
                                level.levelIndex === 0
                                    ? (placeholder ?? "Select group")
                                    : "Select sub group"
                            }
                            onChange={(group?: ClassifierGroup) =>
                                handleLevelChange(level.levelIndex, group)
                            }
                        />
                    </Box>
                ))
            )}
        </Stack>
    );
}

export default RecursiveGroupSelector;
